/**
 * RAM → Cockpit portal-handoff token extraction.
 *
 * This module is intentionally dependency-free and runs its extraction AT IMPORT TIME:
 * it must execute before `@/router` and `@/external/external.routes` are evaluated, because
 * `createWebHistory()` snapshots `window.location` (including the hash) when the router is
 * created. A leftover `#_portal_handoff=…` in that snapshot sends the `${fullPath}/detail`
 * redirects on `/mfhs/:mfhId` into infinite recursion (the appended segment lands inside the
 * hash, so the path never changes). Keep the import of this module ABOVE any router imports
 * in `main.ts`.
 *
 * The hash fragment carries only an opaque, single-use, 60s token — never a UID or URL — and
 * is stripped immediately so it survives into neither history nor bookmarks.
 */
const PORTAL_HANDOFF_HASH_PARAM = '_portal_handoff';

/** base64url of 32 random bytes is 43 chars; allow a safe range but reject anything non-opaque. */
const HANDOFF_TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

const extractPortalHandoffTokenFromUrl = (): string | null => {
    let token: string | null;
    try {
        const hash = window.location.hash.replace(/^#/, '');
        if (!hash.includes(PORTAL_HANDOFF_HASH_PARAM)) {
            return null;
        }
        token = new URLSearchParams(hash).get(PORTAL_HANDOFF_HASH_PARAM);
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    } catch {
        return null;
    }
    if (!token || !HANDOFF_TOKEN_PATTERN.test(token)) {
        if (token) {
            console.warn('Ignoring malformed portal handoff token in URL');
        }
        return null;
    }
    return token;
};

/** Captured once at boot, before any router exists. Consumed later by `applyPortalHandoff`. */
export const portalHandoffToken: string | null = extractPortalHandoffTokenFromUrl();
